import store from '@/store/store'
import RouterHelper from '@/helpers/routerHelper'
import { createRouter, createWebHistory } from 'vue-router'
import { useCollapse } from '@/composables/useCollapse'

const context = require.context('./routes', false, /^(?!.*Child).*\.js$/)
const routes = []
context.keys().forEach(file => {
  const route = context(file).default || context(file)
  routes.push(...route)
})

const router = createRouter({
  mode: 'history',
  history: createWebHistory(),
  base: process.env.BASE_URL,
  // Diana, 11/09 : « dans le calendrier hangar, parking, quand je clique sur le jour ca ouvre la page
  // DAY, mais au meme niveau de scroll que la page de renvoi ».
  // `{ x: 0, y: 0 }` est la syntaxe de Vue Router 3. Vue Router 4, installe ici, attend `top` et
  // `left` : l'objet etait ignore et la nouvelle page s'ouvrait a la position de defilement de la
  // precedente, sur toutes les pages de l'application.
  // Bouton precedent du navigateur : on restaure la position memorisee, comportement attendu d'un
  // retour arriere. Toute autre navigation ouvre la page en haut.
  scrollBehavior(to, from, savedPosition) {
    return savedPosition || { top: 0, left: 0 }
  },
  routes,
})

// MFC2-1653 - un deploiement remplace les fichiers hashes du build sous les onglets deja ouverts.
// La navigation suivante demande un chunk qui n'existe plus, echoue, et l'utilisateur reste sur sa
// page alors que son enregistrement est deja passe. C'est ce qui a bloque la creation de vol du
// 16/10/2026 : le POST repondait 201, aucun ecran ne changeait, et deux trips ont ete crees.
//
// Le rechargement etait deja prevu ici, mais sa condition ne pouvait pas se declencher : webpack
// emet « Loading chunk 6732 failed. », jamais « chunk chunk-6732 ». On reconnait desormais les
// messages reellement emis : ceux de webpack pour le JS comme pour le CSS extrait, et ceux de
// l'import dynamique natif.
const CHUNK_LOAD_ERROR = /ChunkLoadError|CSS_CHUNK_LOAD_FAILED|Loading (CSS )?chunk \S+ failed|Failed to fetch dynamically imported module|error loading dynamically imported module/i

// Un delai, et non un drapeau leve a la premiere navigation reussie : apres rechargement, le
// chargement initial de la route courante reussit toujours, et lever la garde a ce moment ferait
// boucler un chunk reellement absent. Une tentative par minute laisse passer le cas utile - le
// build a change, le rechargement suffit - et transforme une boucle en erreur visible.
const RELOAD_GUARD_KEY = 'mfc.chunkReloadAt'
const RELOAD_COOLDOWN_MS = 60000

router.onError((error, to) => {
  if (!CHUNK_LOAD_ERROR.test(`${error?.code || ''} ${error?.message || ''}`)) return

  try {
    const lastAttempt = Number(window.sessionStorage.getItem(RELOAD_GUARD_KEY)) || 0
    if (Date.now() - lastAttempt < RELOAD_COOLDOWN_MS) return
    window.sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()))
  } catch {
    // Stockage de session indisponible : sans moyen de memoriser la tentative, on s'abstient
    // plutot que de risquer un rechargement en boucle.
    return
  }

  // Un chargement complet de la DESTINATION, et non un rechargement de la page courante. La
  // navigation qui echoue suit souvent un enregistrement deja effectue : ramener l'utilisateur
  // sur le formulaire qu'il vient de soumettre le conduit a le soumettre une seconde fois, et
  // c'est exactement ainsi que le 16/10 un seul vol voulu a produit deux trips. Arriver sur la
  // destination lui montre ce qu'il a cree.
  window.location.assign(to?.fullPath || window.location.href)
})
router.beforeEach((to, _from, next) => {
  // TODO
  // If we force to clear local storage and session storage, and refresh, the url is:
  // http://localhost:8890/login?redirect=%5Bobject%20Object%5D, and keep the login page after the login, who successed. How fix it?
  if (store.state.auth.isAuthorized) {
    // MFC2-1631 (Diana 2026-07-27) — RETRAIT du fix #2 (isPublicRoute testé avant canNavigate quand
    // connecté). Ce fix permettait à un user connecté d'ouvrir la route publique 'registration' (le
    // tunnel) en contournant canNavigate, mais cela entrait en contradiction avec la page not-authorized
    // (ouverture d'une route publique "en fin de chaîne orga+user+méthodes"). On revient au comportement
    // d'origine : canNavigate décide. Le tunnel reste accessible librement par le LIEN EMAIL (flux
    // anonyme, branche else-if isPublicRoute non touchée).
    if (RouterHelper.canNavigate(to)) next()
    else next({ name: 'not-authorized', query: { redirect: to.path } })
  } else if (RouterHelper.isPublicRoute(to.name)) {
    next()
  } else {
    next({ name: 'login', query: { redirect: to.path } })
  }
})

router.afterEach(() => {
  const { clearAllCollapse } = useCollapse()
  clearAllCollapse()
})

export default router
